import { useTranslation } from "react-i18next";
import React, { useState, useRef, useEffect, memo, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { FilePdfOutlined, Skeleton } from "src/assets/imports/antdComponents";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "./style.css";

// ── Worker setup ──────────────────────────────────────────────────────────────
pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

// ── Custom debounce hook (no external package needed) ─────────────────────────
function useDebounce<T>(value: T, delay: number): T {
	const [debouncedValue, setDebouncedValue] = useState<T>(value);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedValue(value), delay);
		return () => clearTimeout(timer);
	}, [value, delay]);

	return debouncedValue;
}

// ── Memoized single page ──────────────────────────────────────────────────────
interface PdfPageProps {
	index: number;
	scale: number;
	pageNumber: number;
	pageRef: (el: HTMLDivElement | null) => void;
	getPageStyle: (index: number) => React.CSSProperties;
}

const PdfPage = memo(
	({ index, scale, pageNumber, pageRef, getPageStyle }: PdfPageProps) => (
		<div ref={pageRef} style={getPageStyle(index)}>
			<Page scale={scale} pageNumber={index + 1} renderTextLayer={false} renderAnnotationLayer={false} loading={false} className="pdf-page" />
		</div>
	),
	(prev, next) => prev.scale === next.scale && prev.pageNumber === next.pageNumber && prev.index === next.index,
);

// ── Main component ────────────────────────────────────────────────────────────
const ReportPreview = (props) => {
	const { t } = useTranslation();
	const { loading, pageNo, reportUrl, setLoading, scale } = props;

	const [numPages, setNumPages] = useState<number>(0);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [error, setError] = useState<string | null>(null);

	// ✅ Debounce scale — waits 150ms after last zoom action before re-rendering
	const debouncedScale = useDebounce(scale, 150);

	const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

	useEffect(() => {
		if (numPages > 0 && pageNumber > 0 && pageRefs.current[pageNumber - 1]) {
			const timer = setTimeout(() => {
				pageRefs.current[pageNumber - 1]?.scrollIntoView({ behavior: "smooth" });
			}, 1200);
			return () => clearTimeout(timer);
		}
	}, [numPages, pageNumber]);

	const onDocumentLoadSuccess = useCallback(
		({ numPages }: { numPages: number }) => {
			setNumPages(numPages);
			setLoading(false);
			setPageNumber(pageNo);
		},
		[pageNo, setLoading],
	);

	const onLoadError = useCallback(
		(error: Error) => {
			setError(error.message);
			setLoading(false);
		},
		[setLoading],
	);

	// ✅ Memoized — only recalculates when active pageNumber changes
	const getPageStyle = useCallback(
		(index: number): React.CSSProperties => ({
			marginBottom: "0.5rem",
			marginTop: "0.5rem",
			textAlign: "center",
			border: pageNumber === index + 1 ? "12px solid #fffae5" : "none",
			transition: "border 0.3s ease-in-out",
		}),
		[pageNumber],
	);

	return (
		<div>
			{loading && (
				<div className="parent-container">
					<Skeleton.Node style={{ width: "100%", height: "100%" }} active={true}>
						<FilePdfOutlined style={{ fontSize: 60, color: "#bfbfbf" }} />
					</Skeleton.Node>
				</div>
			)}
			<div className={scale <= 1 ? "pdf-container" : "pdf-container-zoomed"}>
				<Document file={reportUrl} onLoadSuccess={onDocumentLoadSuccess} onLoadError={onLoadError} loading={<div style={{ display: "none" }} />} noData={""}>
					{Array.from(new Array(numPages), (_, index) => (
						<PdfPage
							key={`page_${index + 1}`}
							index={index}
							scale={debouncedScale} // ✅ debounced scale
							pageNumber={pageNumber}
							pageRef={(el) => (pageRefs.current[index] = el)}
							getPageStyle={getPageStyle} // ✅ memoized style fn
						/>
					))}
				</Document>
			</div>
		</div>
	);
};

export default memo(ReportPreview);
